import { BasicButton, OTP, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import env from "@/env.config";
import { resendOTP } from "@/redux/reducers/auth";
import {
	AccountSettingVerifyPhoneNumberFieldsSchema,
	SearchParamsAccountSettingVerifyPhoneNumber,
} from "@/schemas";
import { verificationService } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { zodSearchValidator } from "@tanstack/router-zod-adapter";
import { useEffect } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { toast } from "sonner";

export const Route = createFileRoute(
	"/dashboard/accounts/settings/account/verify-phone-number",
)({
	validateSearch: zodSearchValidator(
		SearchParamsAccountSettingVerifyPhoneNumber,
	),
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const search = Route.useSearch({
		select: (state: SearchParamsAccountSettingVerifyPhoneNumber) => state,
	});
	const dispatch = useDispatch();
	const {
		control,
		handleSubmit,
		setValue,
		formState: { isValid, isSubmitting },
	} = useForm<AccountSettingVerifyPhoneNumberFieldsSchema>({
		mode: "all",
		resolver: zodResolver(AccountSettingVerifyPhoneNumberFieldsSchema),
	});

	const { mutateAsync } = useMutation({
		mutationFn: verificationService.verify.bind(verificationService),
	});

	const onSubmit: SubmitHandler<AccountSettingVerifyPhoneNumberFieldsSchema> = (
		data,
		e,
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		return mutateAsync(
			{ verify_code: data.otp },
			{
				onSuccess: () => {
					toast.success("Phone number updated");
					navigate({
						to: "/dashboard/accounts/settings/account",
					});
				},
			},
		);
	};

	const onClickResend = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		if (!search.phone_number) return;
		return dispatch(
			resendOTP({
				phone_number: search.phone_number,
				callback: (res: { otp: string }) => {
					toast.success("Code resent successfully");
					if (!env.isProd && res.otp) {
						navigate({
							to: "/dashboard/accounts/settings/account/verify-phone-number",
							search: {
								...search,
								otp: res.otp,
							},
							replace: true,
						});
					}
				},
			}),
		);
	};

	useEffect(() => {
		if (search.otp) {
			setValue("otp", search.otp, {
				shouldDirty: true,
				shouldValidate: true,
				shouldTouch: true,
			});
		}
	}, [search.otp, setValue]);

	return (
		<div className="wrapper !p-0 min-h-full mx-auto">
			<BackAppBar />
			<form onSubmit={handleSubmit(onSubmit)} className="container">
				<Stack spacing={4}>
					<Typography fontWeight={600} fontSize={28}>
						Verify phone number
					</Typography>
					<Typography fontWeight={400} fontSize={14}>
						Insert your SMS code to verify your mobile device.
					</Typography>
					<Stack justifyContent="center" alignItems="center">
						<Controller
							control={control}
							name="otp"
							defaultValue={""}
							render={({ field }) => (
								<OTP
									separator={<span> </span>}
									value={field.value}
									onChange={field.onChange}
									disable={!!search.otp}
									length={4}
								/>
							)}
						/>
					</Stack>
					<Stack direction="row" justifyContent="center" alignItems="center">
						<Typography fontWeight={400} fontSize={14}>
							Did not receive a code?
						</Typography>
						<Button
							type="button"
							variant="text"
							color="primary"
							className="uppercase"
							onClick={onClickResend}
						>
							Send again
						</Button>
					</Stack>
				</Stack>
				<Box className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
					<Box className="wrapper mx-auto">
						<BasicButton
							type="submit"
							variant="contained"
							color="secondary"
							className="w-full !h-12 text-sm font-semibold !shadow-none"
							disabled={!isValid}
							loading={isSubmitting}
						>
							Update
						</BasicButton>
					</Box>
				</Box>
			</form>
		</div>
	);
}
